import React, { FC } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Badge, Icon } from '@kontentino/ui';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons/faBuilding';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { PortfolioColumn, countLabel } from './portfolioColumns';

type RowProps = {
  column: PortfolioColumn;
  isOpen: boolean;
  // `selected/connectable`; both absent when the row shows no count.
  selected?: number;
  connectable?: number;
  onOpen: (key: string) => void;
  rowRef?: React.Ref<HTMLButtonElement>;
};

const PortfolioRow: FC<RowProps> = ({
  column,
  isOpen,
  selected,
  connectable,
  onOpen,
  rowRef,
}) => {
  const { t } = useTranslation();
  const label = column.isOutside
    ? t('newSocialMediaProfile.oboTokenType.outsidePortfoliosHeading')
    : column.label;
  const hasCount = selected !== undefined && connectable !== undefined;

  return (
    <button
      type="button"
      ref={rowRef}
      onClick={() => onOpen(column.key)}
      aria-current={isOpen ? 'true' : undefined}
      data-cy={`two-column-picker_portfolio-row_${column.key}`}
      className={clsx(
        'tw-flex tw-w-full tw-shrink-0 tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-text-left tw-transition-colors',
        isOpen
          ? 'tw-border-primary-100 tw-bg-primary-10'
          : 'tw-border-grayscale-20 tw-bg-white hover:tw-border-grayscale-50',
      )}
    >
      <Icon icon={faBuilding} className="tw-shrink-0 tw-text-grayscale-100" />
      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
        <span className="tw-truncate tw-text-md tw-font-semibold tw-text-grayscale-180">
          {label}
        </span>
        {(column.isNotAdmin || column.isPartial) && (
          <span className="tw-flex tw-flex-wrap tw-gap-1">
            {column.isNotAdmin && (
              <Badge
                variant="gray"
                data-cy="select-social-profiles_business-not-admin-badge"
              >
                {t('newSocialMediaProfile.oboTokenType.notAdminBadge')}
              </Badge>
            )}
            {column.isPartial && (
              <Badge
                variant="gray"
                className="tw-bg-warning-10 tw-text-warning-120"
                data-cy="select-social-profiles_business-partial-badge"
              >
                {t('newSocialMediaProfile.oboTokenType.partialBadge')}
              </Badge>
            )}
          </span>
        )}
      </span>
      {hasCount && (
        <Badge
          variant={(selected ?? 0) > 0 ? 'primary' : 'gray'}
          className="tw-shrink-0"
          data-cy="two-column-picker_portfolio-count"
        >
          {selected}/{connectable}
        </Badge>
      )}
      <Icon
        icon={faChevronRight}
        className="tw-shrink-0 tw-text-grayscale-100"
      />
    </button>
  );
};

// Memoised: with a hundred portfolios, ticking a page re-renders only the row
// whose count changed (and the open row when another one opens).
const MemoPortfolioRow = React.memo(PortfolioRow);

type Props = {
  columns: PortfolioColumn[];
  openKey: string | null;
  selectedIds: ReadonlySet<string>;
  // Must be stable, or every row re-renders on every change.
  onOpen: (key: string) => void;
  // The scroller, which the picker resets to the top after a search.
  listRef: React.Ref<HTMLDivElement>;
  // The open row, which the picker scrolls into view.
  openRowRef: React.Ref<HTMLButtonElement>;
  className?: string;
};

// The left column's list: its own scroller.
const PortfolioList: FC<Props> = ({
  columns,
  openKey,
  selectedIds,
  onOpen,
  listRef,
  openRowRef,
  className,
}) => (
  <div
    ref={listRef}
    data-cy="two-column-picker_portfolio-list"
    className={clsx('tw-flex tw-flex-col tw-gap-2', className)}
  >
    {columns.map((column) => {
      // The outside row never shows a count, even when one of its pages can
      // be ticked (a page that carries no portfolio).
      const count = column.isOutside ? null : countLabel(column, selectedIds);
      const isOpen = column.key === openKey;

      return (
        <MemoPortfolioRow
          key={column.key}
          column={column}
          isOpen={isOpen}
          selected={count?.selected}
          connectable={count?.connectable}
          onOpen={onOpen}
          rowRef={isOpen ? openRowRef : undefined}
        />
      );
    })}
  </div>
);

export default PortfolioList;
